import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { KeyOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons'
import { App as AntdApp, Button, Dropdown, Tooltip, Typography, type MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { ChangePasswordModal } from '@/features/account/ChangePasswordModal'
import { useAuth } from '@/shared/auth/useAuth'

/**
 * Profil menyusi — headerdagi odam ikonkasi ostidagi dropdown.
 *
 * <h2>Nega bu komponent paydo bo'ldi — XSB-008</h2>
 * Tashqi tester (2026-08-13): «Profil ikonkasi bosilganda profil menyusi
 * ochilmaydi». Ikonka o'sha paytda TO'G'RIDAN-TO'G'RI parol almashtirish
 * oynasini ochardi, chiqish esa YONIDAGI ikkinchi ikonkada edi.
 *
 * ⚠️ Ilgari bu «ataylab shunday» deb hujjatlashtirilgan edi: «ikonka ostida
 * bitta amal bor, bitta band uchun menyu ortiqcha qadam». Egasi 2026-08-13 da
 * boshqacha qaror qildi — haqiqiy menyu bo'lsin. Sabab shu izohning o'zida
 * yozib qoldirilgan edi: amal BITTA emas edi. Chiqish ham bor, faqat u
 * boshqa tugmada turardi.
 *
 * <h2>Menyuga NIMA kirdi va NEGA</h2>
 *
 * <b>1. Kim ekaningiz</b> — `displayName`, undan farq qilsa login ham.
 * Bosilmaydi (`type: 'group'` sarlavhasi, menyu bandi EMAS — klaviatura
 * bilan yurganda ustiga fokus tushmaydi). Sabab: kabinet bitta brauzerda
 * ikki abonent uchun ochilishi mumkin (oilada bitta kompyuter, kafeda
 * umumiy qurilma) va «men kim bo'lib kirganman» degan savolga boshqa
 * hech qayerda javob yo'q edi.
 *
 * <b>2. Parolni almashtirish</b> — ilgari ikonkaning o'zi ochardi.
 *
 * <b>3. Chiqish</b> — ⚠️ ALOHIDA TUGMASI OLIB TASHLANDI, menyuga ko'chdi.
 * Bu shunchaki «tartibga solish» emas: eski kodning o'z izohida chiqish
 * tasdig'i NEGA qo'shilgani yozilgan edi — «tugma profil tugmasining
 * yonida va faqat ikonka, abonent parolni o'zgartirmoqchi bo'lib
 * yonidagisiga tegib ketishi juda oson». Ya'ni ikki yonma-yon ikonka
 * nuqsonning MANBAI edi, tasdiq oynasi esa uning ustiga qo'yilgan yamoq.
 * Menyuda ikkalasi ham YOZUVLI qator bo'ldi va orasida ajratgich bor —
 * adashish uchun sabab qolmadi.
 *
 * ⚠️ TASDIQ OYNASI BARIBIR QOLDI. Menyu xatoni kamaytiradi, nolga
 * tushirmaydi, narxi esa o'zgarmadi: login va parolni qaytadan terish.
 *
 * <h2>⚠️ Til va mavzu tanlagichlari menyuga KO'CHIRILMADI</h2>
 * Ular headerda alohida turibdi va shunday qoladi. Menyuga solinsa har
 * ikkalasi uchun BIR BOSISH QO'SHILARDI, foydasi esa yo'q: mavzu — bitta
 * holatli almashtirgich (bosdingiz — o'zgardi), til esa noto'g'ri tilda
 * ochilib qolgan odamga KO'RINIB turishi kerak. «Profil» yozuvini o'qiy
 * olmayotgan abonent til tanlagichini menyu ichidan topa olmaydi — aynan
 * shu sababdan `LanguageSwitcher` ikonka + `UZ`/`RU`/`EN` matni bilan
 * ochiq turadi.
 *
 * <h2>⚠️ Klaviatura va skrin-rider — `ExpandableHint` bilan bir darajada</h2>
 * · `aria-haspopup="menu"` + `aria-expanded` — tugma menyu ochishini VA
 *   hozir ochiq/yopiqligini aytadi;
 * · `aria-controls` ochiq holatda menyuning `id` siga ishora qiladi;
 * · `autoFocus` — ochilganda fokus menyu ichiga kiradi, ya'ni strelka
 *   bilan yurish darhol ishlaydi;
 * · `Escape` yopadi va fokusni TUGMAGA QAYTARADI. Fokus qaytmasa u
 *   `body` ga tushadi va keyingi `Tab` sahifani boshidan boshlardi.
 *
 * ⚠️ `Escape` ni O'ZIMIZ ushlaymiz. antd `Dropdown` (rc-dropdown/rc-trigger)
 * da `Escape` ishlovchisi ham, `aria-expanded` ham YO'Q — tekshirildi:
 * `node_modules/rc-dropdown` va `@rc-component/trigger` da bu satrlar
 * umuman uchramaydi. Ya'ni «antd o'zi qiladi» deb tashlab ketilsa,
 * klaviatura bilan ishlaydigan odam menyudan chiqa olmasdi.
 *
 * Hujjat ushlagichi: `tests/header.mjs` (manba ustidan) va
 * `tests/ui-full.mjs` §13 (jonli brauzerda).
 */
export function ProfileMenu() {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const { modal } = AntdApp.useApp()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)

  const focusTrigger = useCallback(() => {
    triggerRef.current?.focus()
  }, [])

  /*
    ⚠️ `capture: true` — menyu ichidagi antd komponentlari `Escape` ni
    o'zicha to'xtatib qo'ymasin. Ishlovchi FAQAT menyu ochiq bo'lganda
    osiladi, ya'ni yopiq holatda hech qanday global tugma ushlanmaydi va
    oynalarning (`Modal`) o'z `Escape` xatti-harakati tegilmaydi.
  */
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      focusTrigger()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, focusTrigger])

  /**
   * ⚠️ CHIQISH TASDIQLANADI — sababi komponent izohida (yamoq emas, qatlam).
   * Bekor qilinganda fokus tugmaga qaytadi: menyu bandi allaqachon
   * yo'q qilingan va usiz fokus `body` ga tushib qolardi.
   */
  const confirmSignOut = () => {
    modal.confirm({
      title: t('auth.logoutConfirmTitle'),
      content: t('auth.logoutConfirmText'),
      okText: t('auth.logoutConfirmOk'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () => signOut(),
      onCancel: focusTrigger,
    })
  }

  const items: MenuProps['items'] = [
    {
      key: 'identity',
      type: 'group',
      label: (
        <span style={{ display: 'block', lineHeight: 1.35, paddingBlock: 2 }}>
          <Typography.Text strong style={{ display: 'block', fontSize: 14 }}>
            {user?.displayName ?? ''}
          </Typography.Text>
          {user && user.login !== user.displayName ? (
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {user.login}
            </Typography.Text>
          ) : null}
        </span>
      ),
      children: [
        {
          key: 'password',
          icon: <KeyOutlined />,
          label: t('account.changePassword.title'),
        },
      ],
    },
    { type: 'divider', key: 'separator' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout'),
      danger: true,
    },
  ]

  return (
    <>
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={['click']}
        autoFocus
        menu={{
          id: menuId,
          items,
          onClick: ({ key }) => {
            setOpen(false)
            if (key === 'password') setPasswordModalOpen(true)
            else if (key === 'logout') confirmSignOut()
          },
        }}
      >
        <Tooltip title={t('account.menu.label')}>
          {/* ⚠️ `aria-label` MATNI O'ZGARMASIN — `tests/ui-full.mjs` §13 bu
              tugmani aynan shu yorliq bo'yicha topadi (uch tilda), va
              `tests/header.mjs` kalitning o'zini qo'riqlaydi. */}
          <Button
            ref={triggerRef}
            type="text"
            icon={<UserOutlined />}
            aria-label={t('account.menu.label')}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
          />
        </Tooltip>
      </Dropdown>

      {/* Oyna yopilganda fokus uni ochgan tugmaga qaytadi */}
      <ChangePasswordModal
        open={passwordModalOpen}
        onClose={() => {
          setPasswordModalOpen(false)
          focusTrigger()
        }}
      />
    </>
  )
}
